/**
 * BUSINESS MAPPERS — conversione tra tipi UI (union string) e DTO REST.
 *
 * Pattern identico a taskMappers.ts / personMappers.ts: enum UI in union
 * string, backend int32, conversione ai boundary in businessApi.ts.
 *
 * Fonte di verita' dei types Business: `Business`, `BusinessStatus`,
 * `BusinessDto`, `BusinessInput`, `AddressDto` vivono qui. businessTypes
 * re-esporta i types e aggiunge PersonState-like + costanti UI.
 *
 * Note di contratto (rest-openapi.yaml):
 * - `BusinessInput` = { name, domain, owner?, sector?, type?, employees? }.
 *   Niente status, niente portraitURL, niente collection nested.
 * - `Business` response include tutto + `status: int32` (server-driven),
 *   `portraitURL` derivato da imageId, `emailAddresses/phones/addresses`
 *   come collections nested.
 * - `Address` e' nested nel Business in lettura; il CRUD dedicato usa
 *   i path /business/{id}/address[/{addressId}]. Include countryId/regionId
 *   (int64 Liferay, ignorati dal frontend per ora — vedi Step 2e),
 *   latitude/longitude calcolati server-side, primary, name, description.
 */

import { enumFromDto, pickAuditFields, type AuditFields } from '@crm/shared';
import type { EmailAddress } from '../emailAddress/emailAddressTypes';
import type { Phone } from '../phone/phoneTypes';
import type { Address } from '../address/addressTypes';

// ─── Types UI ──────────────────────────────────────────────────────────
//
// SYNC CROSS-LAYER — BusinessStatus e' server-driven (readonly in PATCH).
// Se aggiungi/rimuovi/rinomini un valore, aggiornare IN PARALLELO:
//   - modules/crm-util/.../BusinessStatus.java
//   - BUSINESS_STATUS_TO_INT sotto

export type BusinessStatus = 'inattivo' | 'attivo';

// ─── Tabelle di mapping ────────────────────────────────────────────────
// SYNC with BusinessStatus.java: INACTIVE=0, ACTIVE=1
// File: modules/crm-util/src/main/java/it/dvel/crm/util/constants/BusinessStatus.java
const BUSINESS_STATUS_TO_INT: Record<BusinessStatus, number> = {
  inattivo: 0,
  attivo: 1,
};
const BUSINESS_STATUS_FROM_INT: Record<number, BusinessStatus> = invert(BUSINESS_STATUS_TO_INT);

// ─── API di mapping singolo enum ───────────────────────────────────────
// File-local: usati dentro businessFromDto/toDto. Il fallback soft + warn
// su int fuori-range e' centralizzato in `enumFromDto` (@crm/shared).

const businessStatusToDto = (v: BusinessStatus): number => BUSINESS_STATUS_TO_INT[v];
const businessStatusFromDto = (v: number): BusinessStatus =>
  enumFromDto('BusinessStatus', v, BUSINESS_STATUS_FROM_INT, 'attivo');

// ─── Sub-DTO nested ────────────────────────────────────────────────────

/** Sub-DTO EmailAddress/Phone: shape coincide con UI (niente enum int). */
export interface EmailAddressDto {
  emailAddressId: number;
  address: string;
  primary: boolean;
}
export interface PhoneDto {
  phoneId: number;
  number: string;
  primary: boolean;
}

/**
 * Sub-DTO Address: include campi risolti server-side (country ISO2,
 * region name, latitude/longitude) e i rispettivi id Liferay. Lo
 * frontend scrive solo `street/city/zip/name/description/primary` in
 * input — `countryId/regionId` non vengono mandati (Step 2e) finche'
 * non c'e' un endpoint Country/Region.
 */
export interface AddressDto {
  addressId: number;
  name?: string;
  description?: string;
  street?: string;
  city?: string;
  countryId?: number;
  country?: string;
  regionId?: number;
  region?: string;
  zip?: string;
  latitude?: number;
  longitude?: number;
  primary?: boolean;
}

// ─── Shape UI del Business ─────────────────────────────────────────────

export interface Business extends AuditFields {
  businessId: number;
  externalCode: string | null;
  name: string;
  domain: string;
  owner: string | null;
  sector: string | null;
  type: string | null;
  employees: string | null;
  vatNumber?: string | null;
  fiscalCode?: string | null;
  sdiCode?: string | null;
  pecEmail?: string | null;
  status: BusinessStatus;
  portraitURL: string | null;
  emailAddresses: EmailAddress[];
  phones: Phone[];
  addresses: Address[];
  /** Colonna della bacheca in cui sta l'azienda. 0 = non collocata. Vedi BusinessDto. */
  currentStateId: number;
}

// ─── DTO Business REST ─────────────────────────────────────────────────

export interface BusinessDto {
  businessId: number;
  externalCode: string | null;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  name: string;
  // domain può tornare null dal backend: opzionale + convert-null="false"
  // (più Business senza dominio ammessi). Normalizzato a '' in businessFromDto.
  domain: string | null;
  portraitURL?: string | null;
  owner?: string | null;
  sector?: string | null;
  type?: string | null;
  employees?: string | null;
  vatNumber?: string | null;
  fiscalCode?: string | null;
  sdiCode?: string | null;
  pecEmail?: string | null;
  status: number;
  emailAddresses: EmailAddressDto[];
  phones: PhoneDto[];
  addresses: AddressDto[];
  /**
   * Stato della pipeline in cui l'azienda è collocata: la colonna della bacheca. 0 = non collocata.
   * Non è una colonna di crm_Business — è una riga di PipelineMembership che il backend risolve
   * prima di rispondere. Opzionale perché le risposte precedenti a TS-117 non lo portano.
   */
  currentStateId?: number;
}

/**
 * Payload POST/PATCH /businesses|/business. I campi collection nested,
 * audit, status (server-driven), portraitURL non sono accettati in input.
 */
export interface BusinessInput {
  externalCode?: string | null;
  name?: string;
  domain?: string;
  owner?: string | null;
  sector?: string | null;
  type?: string | null;
  employees?: string | null;
  vatNumber?: string | null;
  fiscalCode?: string | null;
  sdiCode?: string | null;
  pecEmail?: string | null;
  /**
   * Colonna della bacheca in cui spostare l'azienda. 0 la toglie dalla board. Viaggia in questo
   * payload ma lato server passa dal canale dedicato `updateCurrentState`: non è lo `status`
   * dell'azienda e non lo tocca.
   */
  currentStateId?: number;
}

// ─── Whole-DTO mappers ─────────────────────────────────────────────────

/** DTO REST -> modello UI. */
export function businessFromDto(dto: BusinessDto): Business {
  return {
    businessId: dto.businessId,
    externalCode: dto.externalCode ?? null,
    ...pickAuditFields(dto),
    name: dto.name,
    domain: dto.domain ?? '',
    owner: dto.owner ?? null,
    sector: dto.sector ?? null,
    type: dto.type ?? null,
    employees: dto.employees ?? null,
    vatNumber: dto.vatNumber ?? null,
    fiscalCode: dto.fiscalCode ?? null,
    sdiCode: dto.sdiCode ?? null,
    pecEmail: dto.pecEmail ?? null,
    status: businessStatusFromDto(dto.status),
    portraitURL: dto.portraitURL ?? null,
    emailAddresses: dto.emailAddresses as EmailAddress[],
    phones: dto.phones as Phone[],
    addresses: dto.addresses as Address[],
    // `?? 0` e non un default più in alto: 0 è il "non collocata" del contratto, e una risposta che
    // il campo non lo porta affatto significa la stessa cosa.
    currentStateId: dto.currentStateId ?? 0,
  };
}

/**
 * Modello UI -> DTO REST. Usato dai mock MSW per simulare la shape
 * che emette il backend. Non invocato dall'app reale.
 */
export function businessToDto(b: Business): BusinessDto {
  return {
    businessId: b.businessId,
    externalCode: b.externalCode,
    ...pickAuditFields(b),
    name: b.name,
    domain: b.domain,
    owner: b.owner,
    sector: b.sector,
    type: b.type,
    employees: b.employees,
    vatNumber: b.vatNumber ?? null,
    fiscalCode: b.fiscalCode ?? null,
    sdiCode: b.sdiCode ?? null,
    pecEmail: b.pecEmail ?? null,
    status: businessStatusToDto(b.status),
    portraitURL: b.portraitURL,
    emailAddresses: b.emailAddresses,
    phones: b.phones,
    addresses: b.addresses as AddressDto[],
    currentStateId: b.currentStateId,
  };
}

/** Payload create UI -> DTO input. */
export function businessCreateToInput(p: {
  externalCode?: string;
  name: string;
  domain: string;
  owner?: string;
  sector?: string;
  type?: string;
  employees?: string;
  vatNumber?: string;
  fiscalCode?: string;
  sdiCode?: string;
  pecEmail?: string;
}): BusinessInput {
  return {
    externalCode: p.externalCode ?? null,
    name: p.name,
    domain: p.domain,
    owner: p.owner ?? null,
    sector: p.sector ?? null,
    type: p.type ?? null,
    employees: p.employees ?? null,
    vatNumber: p.vatNumber ?? null,
    fiscalCode: p.fiscalCode ?? null,
    sdiCode: p.sdiCode ?? null,
    pecEmail: p.pecEmail ?? null,
  };
}

/** Payload update UI -> DTO input parziale (PATCH). */
export function businessUpdateToInput(p: {
  externalCode?: string | null;
  name?: string;
  domain?: string;
  owner?: string | null;
  sector?: string | null;
  type?: string | null;
  employees?: string | null;
  vatNumber?: string | null;
  fiscalCode?: string | null;
  sdiCode?: string | null;
  pecEmail?: string | null;
  currentStateId?: number;
}): BusinessInput {
  const out: BusinessInput = {};
  if (p.externalCode !== undefined) out.externalCode = p.externalCode;
  if (p.name !== undefined) out.name = p.name;
  if (p.domain !== undefined) out.domain = p.domain;
  if (p.owner !== undefined) out.owner = p.owner;
  if (p.sector !== undefined) out.sector = p.sector;
  if (p.type !== undefined) out.type = p.type;
  if (p.employees !== undefined) out.employees = p.employees;
  if (p.vatNumber !== undefined) out.vatNumber = p.vatNumber;
  if (p.fiscalCode !== undefined) out.fiscalCode = p.fiscalCode;
  if (p.sdiCode !== undefined) out.sdiCode = p.sdiCode;
  if (p.pecEmail !== undefined) out.pecEmail = p.pecEmail;
  if (p.currentStateId !== undefined) out.currentStateId = p.currentStateId;
  return out;
}

/**
 * DTO input (PATCH body) -> Partial<Business> UI. Usato dai mock MSW per
 * applicare il patch al mockBusinesses storage (che tiene la shape UI).
 */
export function businessInputFromDto(input: BusinessInput): Partial<Business> {
  const out: Partial<Business> = {};
  if (input.externalCode !== undefined)
    out.externalCode = input.externalCode;
  if (input.name !== undefined) out.name = input.name;
  if (input.domain !== undefined) out.domain = input.domain;
  if (input.owner !== undefined) out.owner = input.owner;
  if (input.sector !== undefined) out.sector = input.sector;
  if (input.type !== undefined) out.type = input.type;
  if (input.employees !== undefined) out.employees = input.employees;
  if (input.vatNumber !== undefined) out.vatNumber = input.vatNumber;
  if (input.fiscalCode !== undefined) out.fiscalCode = input.fiscalCode;
  if (input.sdiCode !== undefined) out.sdiCode = input.sdiCode;
  if (input.pecEmail !== undefined) out.pecEmail = input.pecEmail;
  if (input.currentStateId !== undefined) out.currentStateId = input.currentStateId;
  return out;
}

// ─── Helper ────────────────────────────────────────────────────────────

function invert<K extends string>(src: Record<K, number>): Record<number, K> {
  const out = {} as Record<number, K>;
  for (const key of Object.keys(src) as K[]) out[src[key]] = key;
  return out;
}
